import React, { Fragment, useCallback, useEffect, useState } from "react";

import CustomFormItem from "components/basic/FormWrapper/FormItemWrapper";
import UkAutoCompleteAddressInput from "components/basic/SignUpForm/UkAutoCompleteAddressInput";
import FloatInput from "components/inputs/Form/Inputs/FloatInput";
import { isPostcodeValid } from "utils/validation";

import { Question } from "../../constants/questions";
import { QuestionType } from "../../enums/enum";

type ErrorsT = {
    [key: string]: string;
};

interface AddressQuestionProps {
    data: Question;
    onAnswer: (_question: string, _answer: string | undefined, _id: string) => void;
    initialValue: any;
    setContinueDisabled: (_value: boolean) => void;
}

/* eslint-disable no-unused-vars */
enum AddressQuestionInputIds {
    ADDRESS1 = "address1",
    ADDRESS2 = "address2",
    CITY = "city",
    POSTCODE = "postcode",

}
/* eslint-enable no-unused-vars */

const inputs = [
    {
        id: AddressQuestionInputIds.ADDRESS1,
        depend: [],
        name: "Address Line 1",
        required: true,
        type: QuestionType.textQuestion,
    },
    {
        id: AddressQuestionInputIds.ADDRESS2,
        depend: [],
        name: "Address Line 2",
        required: false,
        type: QuestionType.textQuestion,
    },
    {
        id: AddressQuestionInputIds.CITY,
        depend: [],
        name: "Town/City",
        required: true,
        type: QuestionType.textQuestion,
    },
    {
        id: AddressQuestionInputIds.POSTCODE,
        depend: [],
        name: "Postcode",
        required: true,
        type: QuestionType.textQuestion,
    },
];

const AddressQuestion: React.FC<AddressQuestionProps> = ({
    data,
    onAnswer,
    initialValue,
    setContinueDisabled,
}) => {
    const [simpleAddressMode, setSimpleAddressMode] = useState(true);
    const [inputData, setInputData] = useState<any>({});
    const [errors, setErrors] = useState<ErrorsT>({});

    const validate = useCallback(() => {
        const newErrors: Record<string, any> = {};
        inputs?.forEach(input => {
            const inputValue = inputData[input.id];

            if (input.id === AddressQuestionInputIds.POSTCODE) {
                const postcodeValid = isPostcodeValid(inputValue);
                if (!postcodeValid) {
                    newErrors[input.id] = "Please enter a valid postcode";
                }
            }

            if (input.required && !inputValue) {
                newErrors[input.id] = "This field is required";
            }
        });
        return newErrors;
    }, [inputData]);

    const handleInputChange = useCallback(
        ({ name, value, object } : {name?: string, value?: string, object?: {[key: string]: string}}) => {
            if (name) {
                const updatedObject = { ...inputData, [name]: value?.trim() };
                setInputData(updatedObject);
            } else if (object) {
                const updatedObject = { ...inputData, ...object };
                setInputData(updatedObject);
            } else {
                throw new Error("Name or object are required");
            }
        },
        [inputData]
    );

    useEffect(() => {
        const errors = validate();
        setErrors(errors);
        if (Object.keys(errors).length > 0) {
            setContinueDisabled(true);
        } else {
            setContinueDisabled(false);
            onAnswer(data.question, inputData, data.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputData, validate]);

    useEffect(() => {
        if (typeof initialValue === "object" && initialValue !== null) {
            setInputData(initialValue);
            setSimpleAddressMode(false);
        } else {
            // fallback to raw string (legacy address string)
            setInputData({ [QuestionType.addressQuestion]: initialValue });
            setSimpleAddressMode(true);
        }
    }, [initialValue]);

    return (
        <div className="new-questionnaire__question w335px mAuto">
            {data.questionTitle && (
                <div className="new-questionnaire__list-question-title" >{data.questionTitle}</div>
            )}
            <div className="new-questionnaire__question-text mb10" style={data.questionTitle ? { margin: "4px auto" } : {}}>
                {data.question}
            </div>
            {simpleAddressMode
                ? <CustomFormItem
                    error={""}
                >
                    <UkAutoCompleteAddressInput
                        style={{
                            marginBottom: "5px",
                        }}
                        label={""}
                        value={inputData[QuestionType.addressQuestion]}
                        formData={inputData}
                        setSimpleAddressMode={setSimpleAddressMode}
                        addressChange={(address: {[key: string]: string}) => {
                            handleInputChange({
                                object: address,
                            });
                        }}
                        onChange={(value: string, object?: {[key: string]: string}) => {
                            handleInputChange({
                                name: QuestionType.addressQuestion,
                                value,
                                object,
                            });
                        }}
                    />
                </CustomFormItem>
                : <Fragment key={QuestionType.addressQuestion}>
                    {inputs?.map((innerInput, index) => (
                        <CustomFormItem
                            key={innerInput.id}
                            error={errors[innerInput.id]}
                        >
                            <FloatInput
                                index={index}
                                label={innerInput.name}
                                value={inputData[innerInput.id]}
                                error={errors[innerInput.id]}
                                onChange={(event: any) => {
                                    if (!inputData[QuestionType.addressQuestion]) {
                                        handleInputChange({
                                            name: QuestionType.addressQuestion,
                                            value: "manual",
                                        });
                                    }
                                    handleInputChange({
                                        name: innerInput.id,
                                        value: event.target.value,
                                    });
                                }}
                            />
                        </CustomFormItem>
                    ))}
                </Fragment>}
        </div>
    );
};

export default AddressQuestion;
